import { FEATURE_CODE, PRODUCT_CODE } from '../constants';
import { PROFILE_CATEGORY, type WorkspaceProfile } from './types';

const TEAM_SIZE_QUESTION = {
  key: 'household_size',
  label: '¿Cuántas personas usarán esta cuenta?',
  type: 'select' as const,
  options: [
    { value: '1', label: 'Sólo yo' },
    { value: '2-4', label: '2 a 4 personas' },
    { value: '5+', label: '5 o más' },
  ],
};

export const PERSONAL_PROFILES: WorkspaceProfile[] = [
  {
    code: 'PERSONAL_GENERAL',
    productCode: PRODUCT_CODE.PERSONAL,
    category: PROFILE_CATEGORY.PERSONAL,
    name: 'Uso personal',
    description: 'Documentos, fotos y respaldos de tu día a día.',
    icon: '🏠',
    sortOrder: 10,
    terminology: {
      workspace: 'Mi espacio',
      folder: 'Carpeta',
      folderPlural: 'Carpetas',
      file: 'Archivo',
      filePlural: 'Archivos',
    },
    folderTemplate: [
      { name: 'Documentos', children: [{ name: 'Identificación' }, { name: 'Recibos' }] },
      { name: 'Fotos' },
      { name: 'Videos' },
      { name: 'Respaldos' },
    ],
    recommendedFeatures: [FEATURE_CODE.SHARED_LINKS, FEATURE_CODE.PHOTO_GALLERY],
    metadataFields: [],
    onboardingQuestions: [
      {
        key: 'primary_use',
        label: '¿Qué guardarás principalmente?',
        type: 'multiselect',
        options: [
          { value: 'documents', label: 'Documentos' },
          { value: 'photos', label: 'Fotografías' },
          { value: 'videos', label: 'Videos' },
          { value: 'backups', label: 'Respaldos del teléfono' },
        ],
      },
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'jpg', 'png', 'mp4', 'docx'],
  },
  {
    code: 'FAMILY',
    productCode: PRODUCT_CODE.PERSONAL,
    category: PROFILE_CATEGORY.PERSONAL,
    name: 'Familia',
    description: 'Un espacio compartido para documentos y recuerdos familiares.',
    icon: '👨‍👩‍👧',
    sortOrder: 20,
    terminology: {
      workspace: 'Espacio familiar',
      folder: 'Carpeta',
      folderPlural: 'Carpetas',
      file: 'Archivo',
      filePlural: 'Archivos',
    },
    folderTemplate: [
      {
        name: 'Documentos familiares',
        children: [
          { name: 'Actas y registros' },
          { name: 'Pasaportes e identificaciones' },
          { name: 'Seguros' },
        ],
      },
      { name: 'Fotos', children: [{ name: 'Viajes' }, { name: 'Celebraciones' }] },
      { name: 'Videos' },
      { name: 'Salud' },
      { name: 'Escuela' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.PHOTO_GALLERY,
      FEATURE_CODE.PHOTO_BACKUP,
      FEATURE_CODE.FOLDER_SHARING,
    ],
    metadataFields: [
      {
        key: 'family_member',
        label: 'Miembro de la familia',
        type: 'text',
        appliesTo: 'BOTH',
      },
    ],
    onboardingQuestions: [
      TEAM_SIZE_QUESTION,
      {
        key: 'shared_albums',
        label: '¿Quieres álbumes de fotos compartidos con la familia?',
        type: 'boolean',
      },
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['jpg', 'png', 'heic', 'mp4', 'pdf'],
  },
  {
    code: 'PHOTOGRAPHY',
    productCode: PRODUCT_CODE.PERSONAL,
    category: PROFILE_CATEGORY.CREATIVE,
    name: 'Fotografía',
    description: 'Sesiones, originales y entregas a clientes.',
    icon: '📷',
    sortOrder: 30,
    terminology: {
      workspace: 'Mi estudio',
      folder: 'Sesión',
      folderPlural: 'Sesiones',
      file: 'Foto',
      filePlural: 'Fotos',
    },
    folderTemplate: [
      { name: 'Sesiones', children: [{ name: 'RAW' }, { name: 'Editadas' }] },
      { name: 'Entregas a cliente' },
      { name: 'Portafolio' },
      { name: 'Contratos' },
    ],
    recommendedFeatures: [
      FEATURE_CODE.PHOTO_GALLERY,
      FEATURE_CODE.SHARED_LINKS,
      FEATURE_CODE.LINK_EXPIRATION,
      FEATURE_CODE.FOLDER_SHARING,
    ],
    metadataFields: [
      { key: 'session_date', label: 'Fecha de la sesión', type: 'date', appliesTo: 'FOLDER' },
      { key: 'client', label: 'Cliente', type: 'text', appliesTo: 'FOLDER' },
      {
        key: 'session_type',
        label: 'Tipo de sesión',
        type: 'select',
        options: ['Retrato', 'Boda', 'Producto', 'Evento', 'Inmobiliaria', 'Otro'],
        appliesTo: 'FOLDER',
      },
    ],
    onboardingQuestions: [
      {
        key: 'delivers_to_clients',
        label: '¿Entregas galerías a clientes?',
        type: 'boolean',
        helpText: 'Activaremos enlaces con caducidad y control de descargas.',
      },
    ],
    sharingDefaults: {
      requirePassword: true,
      expiresInDays: 14,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['jpg', 'png', 'tiff', 'heic', 'mp4'],
  },
  {
    code: 'STUDENT',
    productCode: PRODUCT_CODE.PERSONAL,
    category: PROFILE_CATEGORY.PERSONAL,
    name: 'Estudiante',
    description: 'Apuntes, trabajos y material de estudio organizados por materia.',
    icon: '🎓',
    sortOrder: 40,
    terminology: {
      workspace: 'Mi espacio',
      folder: 'Materia',
      folderPlural: 'Materias',
      file: 'Documento',
      filePlural: 'Documentos',
    },
    folderTemplate: [
      { name: 'Materias' },
      { name: 'Tareas y trabajos' },
      { name: 'Apuntes' },
      { name: 'Certificados' },
    ],
    recommendedFeatures: [FEATURE_CODE.SHARED_LINKS, FEATURE_CODE.FOLDER_SHARING],
    metadataFields: [
      { key: 'subject', label: 'Materia', type: 'text', appliesTo: 'BOTH' },
      { key: 'term', label: 'Periodo', type: 'text', appliesTo: 'BOTH' },
    ],
    onboardingQuestions: [
      {
        key: 'education_level',
        label: 'Nivel de estudios',
        type: 'select',
        options: [
          { value: 'highschool', label: 'Bachillerato' },
          { value: 'undergraduate', label: 'Pregrado' },
          { value: 'graduate', label: 'Posgrado' },
        ],
      },
    ],
    sharingDefaults: {
      requirePassword: false,
      expiresInDays: 30,
      allowDownload: true,
      allowPreview: true,
    },
    commonExtensions: ['pdf', 'docx', 'pptx', 'xlsx', 'jpg'],
  },
];
